<script setup lang="ts">
/**
 * Destructive confirmation overlay, shared by every entity's detail card —
 * both the mobile full-screen views and the desktop `InfoViewLayout` cards
 * (Payment / Client / Service / ServiceType / ServiceCategory) — to confirm
 * delete before it fires.
 *
 * Renders as an iOS-style action sheet anchored to the bottom of the
 * viewport by default (matches docs/sketches/mobile/delete-confirmation.html),
 * or as a screen-centered dialog when `centered` is true (matches
 * docs/sketches/desktop/delete-confirmation.html) — desktop callers pass
 * `centered` since a bottom sheet reads as a mobile pattern there.
 *
 * Presentational only — the host owns the mutation and passes `pending` to
 * disable the buttons while the request is in flight, and `open` to control
 * visibility (kept mounted rather than `v-if`-toggled so the scrim/sheet
 * transition can animate on both open and close).
 */
import { useI18n } from 'vue-i18n'

defineProps<{
  open: boolean
  title: string
  description: string
  confirmLabel?: string
  pending?: boolean
  /** Failure message from the last confirm attempt; shown under the description. */
  error?: string | null
  /** Renders as a centered dialog instead of a bottom-anchored sheet (desktop cards). */
  centered?: boolean
}>()

const emit = defineEmits<{
  confirm: []
  cancel: []
}>()

const { t } = useI18n()
</script>

<template>
  <div
    class="dcs-scrim"
    :class="{ 'dcs-scrim--show': open, 'dcs-scrim--centered': centered }"
    @click="emit('cancel')"
  >
    <div class="dcs-sheet" :class="{ 'dcs-sheet--centered': centered }" @click.stop>
      <div class="dcs-group">
        <div class="dcs-message">
          <div class="dcs-title">{{ title }}</div>
          <div class="dcs-desc">{{ description }}</div>
          <div v-if="error" class="dcs-error" role="alert">{{ error }}</div>
        </div>
        <button type="button" class="dcs-btn" :disabled="pending" @click="emit('confirm')">
          {{ pending ? t('common.deleting') : (confirmLabel ?? t('common.delete')) }}
        </button>
      </div>
      <div class="dcs-group">
        <button
          type="button"
          class="dcs-btn dcs-btn--cancel"
          :disabled="pending"
          @click="emit('cancel')"
        >
          {{ t('common.cancel') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dcs-scrim {
  position: fixed;
  inset: 0;
  z-index: 20;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.18s ease;
}
.dcs-scrim--show {
  opacity: 1;
  pointer-events: auto;
}

/* Desktop variant: dialog centered in the viewport instead of a bottom sheet. */
.dcs-scrim--centered {
  align-items: center;
}

.dcs-sheet {
  width: 100%;
  max-width: 28rem;
  padding: 0 0.5rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  transform: translateY(1rem);
  transition: transform 0.2s ease;
}
.dcs-scrim--show .dcs-sheet {
  transform: translateY(0);
}

.dcs-sheet--centered {
  max-width: 22rem;
  padding: 0.5rem;
  transform: scale(0.96);
  transition: transform 0.15s ease;
}
.dcs-scrim--show .dcs-sheet--centered {
  transform: scale(1);
}

.dcs-group {
  background: color-mix(in srgb, var(--color-tufte-bg) 94%, transparent);
  backdrop-filter: blur(14px);
  border-radius: 13px;
  overflow: hidden;
  text-align: center;
}

.dcs-message {
  padding: 0.9rem 1rem 0.8rem;
  border-bottom: 1px solid var(--color-tufte-border);
}

.dcs-title {
  font-family: var(--font-tufte-sans);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-tufte-text);
  margin-bottom: 0.25rem;
}

.dcs-desc {
  font-family: var(--font-tufte-sans);
  font-size: 0.78rem;
  color: var(--color-tufte-muted);
  line-height: 1.4;
}

.dcs-error {
  font-family: var(--font-tufte-sans);
  font-size: 0.78rem;
  color: var(--color-tufte-danger);
  line-height: 1.4;
  margin-top: 0.5rem;
}

.dcs-btn {
  display: block;
  width: 100%;
  background: none;
  border: none;
  border-bottom: 1px solid var(--color-tufte-border);
  font: inherit;
  font-size: 1.05rem;
  padding: 0.85rem 1rem;
  cursor: pointer;
  color: var(--color-tufte-danger);
}
.dcs-btn:last-child {
  border-bottom: none;
}
.dcs-btn:active {
  background: color-mix(in srgb, var(--color-tufte-danger) 5%, transparent);
}
.dcs-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.dcs-btn--cancel {
  color: var(--color-tufte-text);
  font-weight: 600;
}

/* Desktop variant: action buttons read as uppercase, matching other desktop controls. */
.dcs-sheet--centered .dcs-btn {
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
</style>
